import React, { useMemo, useState, Fragment, createElement } from 'react';
import { Crown, Check, CheckCircle2, ShieldCheck, LayoutGrid, Target, Megaphone, Zap, TrendingUp, ShoppingBag, X, ArrowRight, Clock, Eye, MousePointerClick, DollarSign, ChevronDown, ChevronUp, RotateCcw, CalendarPlus, Rocket, ArrowUpCircle, Mail, Sparkles, BarChart3, Wallet, AlertCircle, Globe, BookOpen, Pin, FileText, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { ALL_AD_SLOTS } from '../ads/adData';
interface PromotionTabProps {
  showToast: (msg: string) => void;
  setActiveAutoService: (service: string | null) => void;
  setActiveTab?: (tab: string) => void;
}
interface AdOrder {
  id: string;
  type: 'banner' | 'email' | 'pin';
  name: string;
  status: 'active' | 'completed' | 'pending_moderation' | 'pending_payment' | 'approved' | 'rejected' | 'pending';
  startDate: string;
  endDate: string | null;
  price: number;
  views: number;
  clicks: number;
  orders: number;
  income: number;
  rejectionReason?: string;
  moderatedAt?: string;
  // Creative materials
  adTitle?: string;
  adDescription?: string;
  adLink?: string;
  adImageUrl?: string;
}
// Map ALL_AD_SLOTS to astrologer-friendly format
const SLOT_ICON_MAP: Record<string, {
  icon: any;
  iconCls: string;
  activeCls: string;
}> = {
  Banner: {
    icon: LayoutGrid,
    iconCls: 'bg-indigo-100 text-indigo-600',
    activeCls: 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500'
  },
  Native: {
    icon: Layers,
    iconCls: 'bg-amber-100 text-amber-600',
    activeCls: 'border-amber-500 bg-amber-50/50 ring-1 ring-amber-500'
  },
  Pinned: {
    icon: Pin,
    iconCls: 'bg-emerald-100 text-emerald-600',
    activeCls: 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
  },
  Email: {
    icon: Mail,
    iconCls: 'bg-purple-100 text-purple-600',
    activeCls: 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500'
  },
  Sticky: {
    icon: Target,
    iconCls: 'bg-rose-100 text-rose-600',
    activeCls: 'border-rose-500 bg-rose-50/50 ring-1 ring-rose-500'
  }
};
export function PromotionTab({
  showToast,
  setActiveAutoService,
  setActiveTab
}: PromotionTabProps) {
  // Dynamic ad slots from shared adData (synced with admin)
  const availableAdSlots = useMemo(() => {
    return ALL_AD_SLOTS.filter(s => s.status === 'active').map(slot => {
      const style = SLOT_ICON_MAP[slot.type] || SLOT_ICON_MAP.Banner;
      const weeklyPrice = Math.round(slot.basePrice / 4);
      const estimatedReach = Math.round(slot.basePrice / 1.5);
      return {
        id: slot.id,
        name: slot.name,
        where: `${slot.page} · ${slot.type} · ${slot.size}`,
        price: `${weeklyPrice.toLocaleString('ru-RU')} ₽ / неделя`,
        priceNum: weeklyPrice,
        reach: `~${estimatedReach.toLocaleString('ru-RU')} показов`,
        requirements: slot.type === 'Pinned' ? 'Профиль заполнен на 80%+' : slot.type === 'Email' ? 'Текст до 200 символов, ссылка на профиль' : `Изображение ${slot.size}, до 50 символов текста`,
        icon: style.icon,
        activeCls: style.activeCls,
        iconCls: style.iconCls,
        slotType: slot.type
      };
    });
  }, []);
  // State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<'pro' | 'top' | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinCategory, setSelectedPinCategory] = useState<string>('');
  const [selectedPinDuration, setSelectedPinDuration] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [showNewAdModal, setShowNewAdModal] = useState(false);
  const [selectedAdSlot, setSelectedAdSlot] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [adOrdersPage, setAdOrdersPage] = useState(1);
  const AD_ORDERS_PER_PAGE = 5;
  // Ad Wizard State
  const [adWizardStep, setAdWizardStep] = useState<1 | 2 | 3>(1);
  const [adMaterials, setAdMaterials] = useState({
    title: '',
    description: '',
    link: '',
    imageUrl: ''
  });
  const [adBalance, setAdBalance] = useState(4500);
  // Mock Data
  const [adOrders, setAdOrders] = useState<AdOrder[]>([{
    id: 'ORD-A-001',
    type: 'banner',
    name: 'Баннер на главной',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '07.03.2026',
    price: 5000,
    views: 12400,
    clicks: 620,
    orders: 18,
    income: 54000,
    adTitle: 'Профессиональный разбор натальной карты',
    adDescription: 'Узнайте свои сильные стороны, кармические задачи и жизненное предназначение. 15 лет опыта, 3000+ консультаций.',
    adLink: '/astrologer/elena-star',
    adImageUrl: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f0?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-002',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'completed',
    startDate: '15.02.2026',
    endDate: '15.02.2026',
    price: 3000,
    views: 10200,
    clicks: 408,
    orders: 12,
    income: 36000,
    adTitle: 'Весенний прогноз 2026 — персональный транзитный отчёт',
    adDescription: 'Специальная акция! Закажите прогноз на весну со скидкой 30%. Узнайте, какие возможности откроются именно для вас.',
    adLink: '/astrologer/elena-star/forecast'
  }, {
    id: 'ORD-A-003',
    type: 'pin',
    name: 'Закреп в каталоге',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '14.03.2026',
    price: 4000,
    views: 8900,
    clicks: 356,
    orders: 9,
    income: 27000,
    adTitle: 'Елена Звёздная — ТОП астролог',
    adDescription: 'Натальные карты, совместимость, прогнозы. Рейтинг 4.9 ★ Более 3000 консультаций.',
    adLink: '/astrologer/elena-star'
  }, {
    id: 'ORD-A-004',
    type: 'banner',
    name: 'Баннер в каталоге',
    status: 'pending_moderation',
    startDate: '10.03.2026',
    endDate: '17.03.2026',
    price: 3500,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    adTitle: 'Кармический анализ — глубокий разбор',
    adDescription: 'Откройте тайны прошлых жизней и поймите свою миссию в этом воплощении. Индивидуальный подход.',
    adLink: '/astrologer/elena-star/karma',
    adImageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-005',
    type: 'pin',
    name: 'Закреп «Натальная карта»',
    status: 'pending_payment',
    startDate: '12.03.2026',
    endDate: '19.03.2026',
    price: 2000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '12.03.2026',
    adTitle: 'Натальная карта Premium — полный разбор личности',
    adDescription: 'PDF-отчёт на 40+ страниц с детальным анализом всех аспектов вашей карты.',
    adLink: '/astrologer/elena-star/natal'
  }, {
    id: 'ORD-A-006',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'rejected',
    startDate: '08.03.2026',
    endDate: null,
    price: 3000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '08.03.2026',
    rejectionReason: 'Текст рекламы содержит запрещённые обещания. Пожалуйста, уберите гарантии результата и отправьте повторно.',
    adTitle: 'Гарантированное изменение судьбы за 1 консультацию!',
    adDescription: 'Я изменю вашу жизнь за одну сессию. 100% гарантия результата. Запишитесь прямо сейчас!',
    adLink: '/astrologer/elena-star'
  }]);
  const activePlacements = [{
    type: 'Закреп в каталоге',
    category: 'Натальная карта',
    daysLeft: 5,
    views: 1247
  }, {
    type: 'Баннер на главной',
    category: 'Все услуги',
    daysLeft: 3,
    views: 4200
  }];
  // Computed
  const totalAdIncome = adOrders.reduce((sum, o) => sum + o.income, 0);
  const totalAdOrders = adOrders.reduce((sum, o) => sum + o.orders, 0);
  const adOrdersTotalPages = Math.ceil(adOrders.length / AD_ORDERS_PER_PAGE);
  const paginatedAdOrders = adOrders.slice((adOrdersPage - 1) * AD_ORDERS_PER_PAGE, adOrdersPage * AD_ORDERS_PER_PAGE);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banner':
        return 'Баннер';
      case 'email':
        return 'Рассылка';
      case 'pin':
        return 'Закреп';
      default:
        return type;
    }
  };
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активен',
          variant: 'success' as const
        };
      case 'completed':
        return {
          label: 'Завершён',
          variant: 'neutral' as const
        };
      case 'pending':
      case 'pending_moderation':
        return {
          label: 'На модерации',
          variant: 'warning' as const
        };
      case 'pending_payment':
        return {
          label: 'Ожидает оплаты',
          variant: 'info' as const
        };
      case 'approved':
        return {
          label: 'Одобрен',
          variant: 'success' as const
        };
      case 'rejected':
        return {
          label: 'Отклонён',
          variant: 'error' as const
        };
      default:
        return {
          label: status,
          variant: 'default' as const
        };
    }
  };
  const handleRepeatOrder = (order: AdOrder) => {
    showToast(`Повторный заказ "${order.name}" создан`);
    console.log(`[Admin Sync] Ad order repeated: ${order.id}`);
  };
  const handleExtendOrder = (order: AdOrder) => {
    showToast(`Продление "${order.name}" оформлено`);
    console.log(`[Admin Sync] Ad order extended: ${order.id}`);
  };
  const handleUpgradeTariff = (tariff: 'pro' | 'top') => {
    setShowTariffModal(false);
    showToast(`Тариф ${tariff === 'pro' ? 'PRO' : 'TOP'} успешно активирован!`);
    console.log(`[Admin Sync] Tariff upgraded to ${tariff}`);
  };
  const handleBuyPin = () => {
    setShowPinModal(false);
    const prices = {
      '1week': 2000,
      '2weeks': 3500,
      '1month': 6000
    };
    showToast(`Закреп "${selectedPinCategory}" оплачен (${prices[selectedPinDuration].toLocaleString('ru-RU')} ₽)`);
    console.log(`[Admin Sync] Pin purchased: ${selectedPinCategory}, duration: ${selectedPinDuration}`);
  };
  return <div className="space-y-6" data-id="element-15036">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-15037">
        <div data-id="element-15038">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-15039">
            Продвижение профиля
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-15040">
            Увеличивайте заказы и доход
          </p>
        </div>
      </div>

      {/* Ad Budget */}
      <Card padding="md" className="border-slate-200 bg-white" data-id="element-15041">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15042">
          <div className="flex-1" data-id="element-15043">
            <div className="flex items-center gap-2 mb-2" data-id="element-15044">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center" data-id="element-15045">
                <Wallet className="w-5 h-5 text-purple-600" data-id="element-15046" />
              </div>
              <div data-id="element-15047">
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-15048">
                  Рекламный баланс
                </h3>
                <p className="text-[10px] text-slate-500" data-id="element-15049">
                  Средства для оплаты рекламных размещений
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mt-3" data-id="element-15050">
              <span className="text-3xl font-bold text-slate-900" data-id="element-15051">
                {adBalance.toLocaleString('ru-RU')} ₽
              </span>
              <Badge variant="success" size="sm" data-id="element-15052">
                доступно
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500" data-id="element-15053">
              <span data-id="element-15054">
                Потрачено за месяц:{' '}
                <strong className="text-slate-700" data-id="element-15055">3 500 ₽</strong>
              </span>
              <span data-id="element-15056">
                Остаток:{' '}
                <strong className="text-slate-700" data-id="element-15057">
                  {adBalance.toLocaleString('ru-RU')} ₽
                </strong>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto" data-id="element-15058">
            <Button variant="primary" size="md" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 shadow-sm" icon={<Wallet className="w-4 h-4" data-id="element-15060" />} onClick={() => {
            setAdBalance(prev => prev + 5000);
            showToast('Рекламный баланс пополнен на 5 000 ₽ ✓');
          }} data-id="element-15059">
              Пополнить рекламный баланс
            </Button>
            <p className="text-[10px] text-slate-400 text-center sm:text-right" data-id="element-15061">
              Минимум 1 000 ₽
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-500" data-id="element-15062">
          <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" data-id="element-15063" />
          <span data-id="element-15064">
            Реклама списывается с рекламного баланса. Если средств не хватает —
            пополните баланс перед оформлением заказа.
          </span>
        </div>
      </Card>

      {/* Section 1: Current Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-15065">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => showToast('Текущий тариф: Базовый')} data-id="element-15066">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-15067">Тариф</div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-15068">
            Базовый <Crown className="w-4 h-4 text-amber-400" data-id="element-15069" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Позиция #47 в каталоге');
      }} data-id="element-15070">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-15071">Позиция</div>
          <div className="text-lg font-bold text-slate-900" data-id="element-15072">#47</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('1 247 просмотров за неделю');
      }} data-id="element-15073">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-15074">
            Просмотры
          </div>
          <div className="text-lg font-bold text-slate-900" data-id="element-15075">1 247</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Конверсия 8.3%');
      }} data-id="element-15076">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-15077">
            Конверсия
          </div>
          <div className="text-lg font-bold text-emerald-600" data-id="element-15078">8.3%</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-emerald-50/50 border-emerald-200" onClick={() => showToast('Доход с продвижения за месяц')} data-id="element-15079">
          <div className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-15080">
            Доход с рекламы
          </div>
          <div className="text-lg font-bold text-emerald-700" data-id="element-15081">
            {totalAdIncome.toLocaleString('ru-RU')} ₽
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-indigo-50/50 border-indigo-200" onClick={() => showToast('Заказы с продвижения за месяц')} data-id="element-15082">
          <div className="text-xs text-indigo-600 mb-1 font-medium" data-id="element-15083">
            Заказы с рекламы
          </div>
          <div className="text-lg font-bold text-indigo-700" data-id="element-15084">
            {totalAdOrders}
          </div>
        </Card>
      </div>

      {/* Section 2: CTA Block — How to increase income */}
      <Card padding="md" className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white border-none relative overflow-hidden" data-id="element-15085">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl" data-id="element-15086"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-15087">
          <div className="flex-1" data-id="element-15088">
            <div className="flex items-center gap-2 mb-2" data-id="element-15089">
              <Sparkles className="w-5 h-5 text-amber-300" data-id="element-15090" />
              <h3 className="text-xl font-bold" data-id="element-15091">Как увеличить доход</h3>
            </div>
            <p className="text-indigo-100 text-sm max-w-lg" data-id="element-15092">
              Перейдите на тариф <strong className="text-white" data-id="element-15093">PRO</strong> и
              получите приоритет в каталоге. По статистике, PRO-астрологи
              получают{' '}
              <strong className="text-white" data-id="element-15094">на 40% больше заказов</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-15095">
            <button className="px-5 py-2.5 bg-white text-indigo-700 font-bold text-sm rounded-lg shadow-lg hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" onClick={() => {
            setSelectedTariff('pro');
            setShowTariffModal(true);
          }} data-id="element-15096">
              <Crown className="w-4 h-4" data-id="element-15097" />
              Перейти на PRO
            </button>
            <button className="px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white font-bold text-sm rounded-lg border-2 border-white/50 hover:bg-white/30 transition-colors flex items-center justify-center gap-2" onClick={() => setShowNewAdModal(true)} data-id="element-15098">
              <Megaphone className="w-4 h-4" data-id="element-15099" />
              Включить рекламу
            </button>
          </div>
        </div>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-15100">
        {[{
        icon: Rocket,
        label: 'Запустить рекламу',
        color: 'text-indigo-600 bg-indigo-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: ArrowUpCircle,
        label: 'Поднять профиль',
        color: 'text-amber-600 bg-amber-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: Mail,
        label: 'Включить рассылку',
        color: 'text-emerald-600 bg-emerald-50',
        action: () => showToast('Рассылка включена — клиенты получат уведомление о ваших услугах')
      }, {
        icon: Crown,
        label: 'Улучшить тариф',
        color: 'text-purple-600 bg-purple-50',
        action: () => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }
      }].map((item, i) => <button key={i} onClick={item.action} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all text-center group" data-id="element-15101">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`} data-id="element-15102">
              <item.icon className="w-5 h-5" data-id="element-15103" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-15104">
              {item.label}
            </span>
          </button>)}
      </div>

      {/* Section 4: Ad Orders */}
      <Card padding="none" className="overflow-hidden" data-id="element-15105">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-15106">
          <div data-id="element-15107">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-15108">
              Мои рекламные заказы
            </h3>
            <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-indigo-600 font-medium hover:underline mt-0.5" data-id="element-15109">
              {showHowItWorks ? 'Скрыть справку' : 'Как работает реклама?'}
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<Megaphone className="w-4 h-4" data-id="element-15111" />} onClick={() => setShowNewAdModal(true)} data-id="element-15110">
            Новый заказ
          </Button>
        </div>
        {showHowItWorks && <div className="px-4 sm:px-5 py-4 bg-indigo-50/50 border-b border-indigo-100 animate-in fade-in slide-in-from-top-2" data-id="element-15112">
            <h4 className="font-bold text-indigo-900 text-sm mb-2" data-id="element-15113">
              Как работает реклама на платформе
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-indigo-800" data-id="element-15114">
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-15115">
                <div className="font-bold mb-1" data-id="element-15116">1. Оформление и оплата</div>
                <p className="text-indigo-600" data-id="element-15117">
                  Выберите формат, загрузите материалы и оплатите с рекламного
                  баланса.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-15118">
                <div className="font-bold mb-1" data-id="element-15119">2. Модерация (1-12 часов)</div>
                <p className="text-indigo-600" data-id="element-15120">
                  Администратор проверит материалы. Результат придет в системные
                  уведомления (колокольчик).
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-15121">
                <div className="font-bold mb-1" data-id="element-15122">3. Запуск и статистика</div>
                <p className="text-indigo-600" data-id="element-15123">
                  После одобрения реклама запустится автоматически. Отслеживайте
                  просмотры и клики здесь.
                </p>
              </div>
            </div>
          </div>}

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-15124">
          <table className="w-full text-left text-sm" data-id="element-15125">
            <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500" data-id="element-15126">
              <tr data-id="element-15127">
                <th className="px-5 py-3 font-semibold" data-id="element-15128">Услуга</th>
                <th className="px-5 py-3 font-semibold" data-id="element-15129">Тип</th>
                <th className="px-5 py-3 font-semibold" data-id="element-15130">Статус</th>
                <th className="px-5 py-3 font-semibold" data-id="element-15131">Период</th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-15132">
                  Стоимость
                </th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-15133">
                  Результат
                </th>
                <th className="px-5 py-3 w-10" data-id="element-15134"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-15135">
              {paginatedAdOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const statusConfig = getStatusConfig(order.status);
              return <Fragment key={order.id} data-id="element-15136">
                    <tr className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/80' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-15137">
                      <td className="px-5 py-3" data-id="element-15138">
                        <div className="font-bold text-slate-900" data-id="element-15139">
                          {order.name}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-15140">
                          {order.id}
                        </div>
                      </td>
                      <td className="px-5 py-3" data-id="element-15141">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-15142">
                          {getTypeLabel(order.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-15143">
                        <Badge variant={statusConfig.variant} size="sm" data-id="element-15144">
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs" data-id="element-15145">
                        {order.endDate ? `${order.startDate} — ${order.endDate}` : `С ${order.startDate}`}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-15146">
                        {order.price.toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="px-5 py-3 text-right" data-id="element-15147">
                        <span className="text-xs text-slate-500" data-id="element-15148">
                          {order.clicks} кликов / {order.orders} заказов
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400" data-id="element-15149">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-15150" /> : <ChevronDown className="w-5 h-5" data-id="element-15151" />}
                      </td>
                    </tr>

                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-15152">
                        <td colSpan={7} className="px-5 py-4" data-id="element-15153">
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2" data-id="element-15154">
                            {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-4" data-id="element-15155">
                                <div className="flex items-center gap-2 mb-2" data-id="element-15156">
                                  {order.status === 'rejected' ? <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-15157" /> : order.status === 'pending_payment' ? <Wallet className="w-5 h-5 text-indigo-500" data-id="element-15158" /> : <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-15159" />}
                                  <h4 className="font-bold text-slate-900 text-sm" data-id="element-15160">
                                    {order.status === 'rejected' ? 'Заказ отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Заказ на модерации'}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-600" data-id="element-15161">
                                  {order.status === 'rejected' ? 'Модерация не пройдена. Исправьте замечания и отправьте повторно.' : order.status === 'pending_payment' ? 'Модерация пройдена! Оплатите заказ для запуска рекламы.' : 'Ваша реклама проверяется администратором. Обычно это занимает 1–12 часов.'}
                                </p>

                                {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-id="element-15162">
                                      <div className="font-bold mb-1" data-id="element-15163">
                                        Причина отклонения:
                                      </div>
                                      {order.rejectionReason}
                                    </div>}

                                <div className="relative pt-2 pb-2" data-id="element-15164">
                                  <div className="absolute left-3 top-6 bottom-4 w-0.5 bg-slate-200" data-id="element-15165"></div>

                                  {/* Шаг 1: Оформлен */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-15166">
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-15167">
                                      <Check className="w-3 h-3" data-id="element-15168" />
                                    </div>
                                    <div data-id="element-15169">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-15170">
                                        Оформлен
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-15171">
                                        {order.startDate}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 2: Модерация */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-15172">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-15173">
                                      {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-15174" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-15175" /> : <Clock className="w-3 h-3" data-id="element-15176" />}
                                    </div>
                                    <div data-id="element-15177">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-15178">
                                        Модерация
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-15179">
                                        {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 3: Оплата */}
                                  <div className={`flex gap-3 mb-3 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-15180">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-15181">
                                      <Wallet className="w-3 h-3" data-id="element-15182" />
                                    </div>
                                    <div data-id="element-15183">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-15184">
                                        Оплата
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : order.status === 'approved' ? 'text-emerald-600' : 'text-slate-500'}`} data-id="element-15185">
                                        {order.status === 'pending_payment' ? 'Ожидает оплаты' : order.status === 'approved' ? 'Оплачено' : 'Ожидается'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 4: Запуск */}
                                  <div className={`flex gap-3 relative z-10 ${order.status !== 'approved' ? 'opacity-40' : ''}`} data-id="element-15186">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-15187">
                                      <Rocket className="w-3 h-3" data-id="element-15188" />
                                    </div>
                                    <div data-id="element-15189">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-15190">
                                        Запуск
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-15191">
                                        Ожидается
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-15192">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-15193">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-15194" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-15195">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-15196">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-15197">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-15198" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-15199">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-15200">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-15201">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-15202">
                                            <Globe className="w-3 h-3" data-id="element-15203" />
                                            <span className="truncate" data-id="element-15204">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2 pt-2" data-id="element-15205">
                                  {order.status === 'pending_payment' && <Button variant="primary" size="sm" icon={<Wallet className="w-3.5 h-3.5" data-id="element-15207" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'active' as const
                            } : o));
                            setAdBalance(prev => prev - order.price);
                            showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                          }} data-id="element-15206">
                                      Оплатить{' '}
                                      {order.price.toLocaleString('ru-RU')} ₽
                                    </Button>}
                                  {order.status === 'rejected' && <Button variant="primary" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-15209" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'pending_moderation' as const,
                              rejectionReason: undefined
                            } : o));
                            showToast('Заявка отправлена повторно на модерацию');
                          }} data-id="element-15208">
                                      Исправить и отправить
                                    </Button>}
                                </div>
                              </div> : <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-5" data-id="element-15210">
                                  <div data-id="element-15211">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-15212">
                                      <Eye className="w-3 h-3" data-id="element-15213" /> Просмотры
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-15214">
                                      {order.views.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-15215">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-15216">
                                      <MousePointerClick className="w-3 h-3" data-id="element-15217" />{' '}
                                      Клики
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-15218">
                                      {order.clicks.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-15219">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-15220">
                                      <ShoppingBag className="w-3 h-3" data-id="element-15221" /> Заказы
                                    </div>
                                    <div className="text-xl font-bold text-indigo-600" data-id="element-15222">
                                      {order.orders}
                                    </div>
                                  </div>
                                  <div data-id="element-15223">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-15224">
                                      <DollarSign className="w-3 h-3" data-id="element-15225" /> Доход
                                    </div>
                                    <div className="text-xl font-bold text-emerald-600" data-id="element-15226">
                                      {order.income.toLocaleString('ru-RU')} ₽
                                    </div>
                                  </div>
                                </div>

                                <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-15227">
                                  CTR:{' '}
                                  <strong className="text-slate-700" data-id="element-15228">
                                    {(order.clicks / order.views * 100).toFixed(1)}
                                    %
                                  </strong>{' '}
                                  · Конверсия:{' '}
                                  <strong className="text-slate-700" data-id="element-15229">
                                    {order.clicks > 0 ? (order.orders / order.clicks * 100).toFixed(1) : 0}
                                    %
                                  </strong>{' '}
                                  · ROI:{' '}
                                  <strong className={order.income > order.price ? 'text-emerald-600' : 'text-rose-600'} data-id="element-15230">
                                    {order.price > 0 ? ((order.income - order.price) / order.price * 100).toFixed(0) : 0}
                                    %
                                  </strong>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mb-4 pt-2" data-id="element-15231">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-15232">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-15233" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-15234">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-15235">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-15236">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-15237" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-15238">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-15239">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-15240">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-15241">
                                            <Globe className="w-3 h-3" data-id="element-15242" />
                                            <span className="truncate" data-id="element-15243">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                <div className="flex gap-2" data-id="element-15244">
                                  <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-15246" />} onClick={e => {
                            e.stopPropagation();
                            handleRepeatOrder(order);
                          }} data-id="element-15245">
                                    Повторить
                                  </Button>
                                  {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-15248" />} onClick={e => {
                            e.stopPropagation();
                            handleExtendOrder(order);
                          }} data-id="element-15247">
                                      Продлить
                                    </Button>}
                                </div>
                              </>}
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-15249">
          {paginatedAdOrders.map(order => {
          const isExpanded = expandedOrderId === order.id;
          const statusConfig = getStatusConfig(order.status);
          return <div key={order.id} data-id="element-15250">
                <div className={`p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-15251">
                  <div className="flex justify-between items-start mb-2" data-id="element-15252">
                    <div data-id="element-15253">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-15254">
                        {order.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-15255">
                        {getTypeLabel(order.type)} · {order.id}
                      </div>
                    </div>
                    <Badge variant={statusConfig.variant} size="sm" data-id="element-15256">
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-15257">
                    <span className="text-xs text-slate-500" data-id="element-15258">
                      {order.startDate}
                      {order.endDate ? ` — ${order.endDate}` : ''}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-15259">
                      {order.price.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>

                {isExpanded && <div className="p-4 bg-slate-50 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-15260">
                    {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-3" data-id="element-15261">
                        <div className="flex items-center gap-2" data-id="element-15262">
                          {order.status === 'rejected' ? <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-15263" /> : order.status === 'pending_payment' ? <Wallet className="w-4 h-4 text-indigo-500" data-id="element-15264" /> : <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-15265" />}
                          <h4 className="font-bold text-slate-900 text-sm" data-id="element-15266">
                            {order.status === 'rejected' ? 'Отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'На модерации'}
                          </h4>
                        </div>

                        {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-700" data-id="element-15267">
                              <div className="font-bold mb-0.5" data-id="element-15268">Причина:</div>
                              {order.rejectionReason}
                            </div>}

                        <div className="relative pt-1 pb-1" data-id="element-15269">
                          <div className="absolute left-3 top-5 bottom-3 w-0.5 bg-slate-200" data-id="element-15270"></div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-15271">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-15272">
                              <Check className="w-3 h-3" data-id="element-15273" />
                            </div>
                            <div data-id="element-15274">
                              <div className="font-bold text-xs text-slate-900" data-id="element-15275">
                                Оформлен
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-15276">
                                {order.startDate}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-15277">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-15278">
                              {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-15279" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-15280" /> : <Clock className="w-3 h-3" data-id="element-15281" />}
                            </div>
                            <div data-id="element-15282">
                              <div className="font-bold text-xs text-slate-900" data-id="element-15283">
                                Модерация
                              </div>
                              <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-15284">
                                {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                              </div>
                            </div>
                          </div>

                          <div className={`flex gap-3 mb-2.5 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-15285">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : 'bg-slate-100 text-slate-400'}`} data-id="element-15286">
                              <Wallet className="w-3 h-3" data-id="element-15287" />
                            </div>
                            <div data-id="element-15288">
                              <div className="font-bold text-xs text-slate-900" data-id="element-15289">
                                Оплата
                              </div>
                              <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : 'text-slate-500'}`} data-id="element-15290">
                                {order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Ожидается'}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 relative z-10 opacity-40" data-id="element-15291">
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-15292">
                              <Rocket className="w-3 h-3" data-id="element-15293" />
                            </div>
                            <div data-id="element-15294">
                              <div className="font-bold text-xs text-slate-900" data-id="element-15295">
                                Запуск
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-15296">
                                Ожидается
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 border-t border-slate-200" data-id="element-15297">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-15298">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-15299" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-15300">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-15301">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-15302">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-15303" />
                                </div>}
                              <div className="p-2.5" data-id="element-15304">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-15305">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-15306">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-15307">
                                    <Globe className="w-2.5 h-2.5" data-id="element-15308" />
                                    <span className="truncate" data-id="element-15309">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex flex-wrap gap-2" data-id="element-15310">
                          {order.status === 'pending_payment' && <Button variant="primary" size="sm" fullWidth icon={<Wallet className="w-3.5 h-3.5" data-id="element-15312" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'active' as const
                    } : o));
                    setAdBalance(prev => prev - order.price);
                    showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                  }} data-id="element-15311">
                              Оплатить {order.price.toLocaleString('ru-RU')} ₽
                            </Button>}
                          {order.status === 'rejected' && <Button variant="primary" size="sm" fullWidth icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-15314" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'pending_moderation' as const,
                      rejectionReason: undefined
                    } : o));
                    showToast('Заявка отправлена повторно на модерацию');
                  }} data-id="element-15313">
                              Исправить и отправить
                            </Button>}
                        </div>
                      </div> : <>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-15315">
                          <div data-id="element-15316">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-15317">
                              Просмотры
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-15318">
                              {order.views.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-15319">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-15320">
                              Клики
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-15321">
                              {order.clicks.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-15322">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-15323">
                              Заказы
                            </div>
                            <div className="font-bold text-indigo-600" data-id="element-15324">
                              {order.orders}
                            </div>
                          </div>
                          <div data-id="element-15325">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-15326">
                              Доход
                            </div>
                            <div className="font-bold text-emerald-600" data-id="element-15327">
                              {order.income.toLocaleString('ru-RU')} ₽
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия (активные/завершённые) */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 mb-3 border-t border-slate-200" data-id="element-15328">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-15329">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-15330" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-15331">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-15332">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-15333">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-15334" />
                                </div>}
                              <div className="p-2.5" data-id="element-15335">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-15336">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-15337">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-15338">
                                    <Globe className="w-2.5 h-2.5" data-id="element-15339" />
                                    <span className="truncate" data-id="element-15340">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex gap-2" data-id="element-15341">
                          <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-15343" />} onClick={e => {
                    e.stopPropagation();
                    handleRepeatOrder(order);
                  }} data-id="element-15342">
                            Повторить
                          </Button>
                          {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-15345" />} onClick={e => {
                    e.stopPropagation();
                    handleExtendOrder(order);
                  }} data-id="element-15344">
                              Продлить
                            </Button>}
                        </div>
                      </>}
                  </div>}
              </div>;
        })}
        </div>

        {/* Ad Orders Pagination */}
        {adOrdersTotalPages > 1 && <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-15346">
            <span className="text-xs text-slate-500" data-id="element-15347">
              Показано {(adOrdersPage - 1) * AD_ORDERS_PER_PAGE + 1}–
              {Math.min(adOrdersPage * AD_ORDERS_PER_PAGE, adOrders.length)} из{' '}
              {adOrders.length}
            </span>
            <Pagination currentPage={adOrdersPage} totalPages={adOrdersTotalPages} onPageChange={setAdOrdersPage} data-id="element-15348" />
          </div>}
      </Card>

      {/* Section 6: Active Placements */}
      <Card padding="md" data-id="element-15349">
        <div className="flex justify-between items-center mb-4" data-id="element-15350">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-15351">
            <Target className="w-5 h-5 text-amber-500" data-id="element-15352" /> Где вы размещены
          </h3>
          <Button variant="outline" size="sm" onClick={() => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }} data-id="element-15353">
            Купить размещение
          </Button>
        </div>

        {activePlacements.length > 0 ? <div className="space-y-3" data-id="element-15354">
            {activePlacements.map((p, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-amber-100 bg-amber-50/30" data-id="element-15355">
                <div className="flex items-center gap-3" data-id="element-15356">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-15357">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-15358" />
                  </div>
                  <div data-id="element-15359">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-15360">
                      {p.type}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-15361">{p.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm" data-id="element-15362">
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-15363">
                    <Clock className="w-3.5 h-3.5 text-amber-500" data-id="element-15364" />
                    <span data-id="element-15365">{p.daysLeft} дн.</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-15366">
                    <Eye className="w-3.5 h-3.5 text-indigo-500" data-id="element-15367" />
                    <span data-id="element-15368">{p.views.toLocaleString('ru-RU')}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 h-8 px-2" onClick={() => {
              setShowPinModal(true);
              setSelectedPinCategory(p.category);
            }} data-id="element-15369">
                    Продлить
                  </Button>
                </div>
              </div>)}
          </div> : <div className="text-center py-8 text-slate-500" data-id="element-15370">
            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-15371" />
            <p className="text-sm" data-id="element-15372">У вас нет активных размещений</p>
          </div>}
      </Card>

      {/* Section 7: Simple Analytics */}
      <Card padding="md" data-id="element-15373">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15374">
          <BarChart3 className="w-5 h-5 text-indigo-500" data-id="element-15375" /> Эффективность за
          месяц
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-15376">
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-15377">
            <Eye className="w-5 h-5 text-slate-400 mx-auto mb-2" data-id="element-15378" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-15379">1 247</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-15380">Просмотры</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-15381">
            <MousePointerClick className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-15382" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-15383">620</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-15384">Клики</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-15385">
            <ShoppingBag className="w-5 h-5 text-indigo-500 mx-auto mb-2" data-id="element-15386" />
            <div className="text-2xl font-bold text-indigo-700" data-id="element-15387">39</div>
            <div className="text-xs text-indigo-600 mt-1" data-id="element-15388">Заказы</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-15389">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-2" data-id="element-15390" />
            <div className="text-2xl font-bold text-emerald-700" data-id="element-15391">117 000 ₽</div>
            <div className="text-xs text-emerald-600 mt-1" data-id="element-15392">Доход</div>
          </div>
        </div>
      </Card>

      {/* Tariff Upgrade Modal */}
      {showTariffModal && selectedTariff && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-15393">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-15394">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-15395">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-15396">
                Переход на {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
              </h3>
              <button onClick={() => setShowTariffModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-15397">
                <X className="w-5 h-5" data-id="element-15398" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-15399">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-15400">
                <div className="flex justify-between text-sm mb-3" data-id="element-15401">
                  <span className="text-slate-500" data-id="element-15402">Тариф</span>
                  <span className="font-bold text-slate-900" data-id="element-15403">
                    {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-15404">
                  <span className="text-slate-500" data-id="element-15405">Стоимость</span>
                  <span className="font-bold text-slate-900" data-id="element-15406">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽ / мес
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-15407">
                  <span className="text-slate-500" data-id="element-15408">Ожидаемый рост заказов</span>
                  <span className="font-bold text-emerald-600" data-id="element-15409">
                    {selectedTariff === 'pro' ? '+40%' : '+120%'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200" data-id="element-15410">
                  <span className="font-bold text-slate-900" data-id="element-15411">
                    К оплате сейчас
                  </span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-15412">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-15413">
                Оплата будет списана с вашего баланса. Тариф активируется
                мгновенно. Отмена возможна в любое время.
              </p>

              <div className="flex gap-3" data-id="element-15414">
                <Button variant="outline" fullWidth onClick={() => setShowTariffModal(false)} data-id="element-15415">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={() => handleUpgradeTariff(selectedTariff)} data-id="element-15416">
                  Оплатить и активировать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* New Ad Order Modal */}
      {showNewAdModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-15417">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto" data-id="element-15418">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-15419">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-15420">
                Создать рекламу
              </h3>
              <button onClick={() => {
            setShowNewAdModal(false);
            setSelectedAdSlot(null);
            setAdWizardStep(1);
            setAdMaterials({
              title: '',
              description: '',
              link: '',
              imageUrl: ''
            });
          }} className="text-slate-400 hover:text-slate-600" data-id="element-15421">
                <X className="w-5 h-5" data-id="element-15422" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-15423">
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6 relative" data-id="element-15424">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-15425"></div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-indigo-500 -z-10 transition-all duration-300" style={{
              width: `${(adWizardStep - 1) * 50}%`
            }} data-id="element-15426"></div>

                {[1, 2, 3].map(step => <div key={step} className="flex flex-col items-center gap-1 bg-white px-2" data-id="element-15427">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${adWizardStep >= step ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`} data-id="element-15428">
                      {adWizardStep > step ? <Check className="w-4 h-4" data-id="element-15429" /> : step}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${adWizardStep >= step ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-15430">
                      {step === 1 ? 'Формат' : step === 2 ? 'Материалы' : 'Оплата'}
                    </span>
                  </div>)}
              </div>

              {adWizardStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-15431">
                  <p className="text-sm text-slate-600" data-id="element-15432">
                    Выберите рекламный слот. Каждый формат показывается в разных
                    местах сайта.
                  </p>

                  <div className="max-h-[45vh] overflow-y-auto space-y-3 pr-1 -mr-1" data-id="element-15433">
                    {availableAdSlots.map(slot => <button key={slot.id} onClick={() => setSelectedAdSlot(selectedAdSlot === slot.id ? null : slot.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAdSlot === slot.id ? slot.activeCls : 'border-slate-200 hover:border-slate-300'}`} data-id="element-15434">
                        <div className="flex items-start gap-3" data-id="element-15435">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${slot.iconCls}`} data-id="element-15436">
                            <slot.icon className="w-5 h-5" data-id="element-15437" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-15438">
                            <div className="flex items-center justify-between gap-2" data-id="element-15439">
                              <span className="font-bold text-slate-900 text-sm" data-id="element-15440">
                                {slot.name}
                              </span>
                              <span className="text-xs font-bold text-indigo-600 whitespace-nowrap" data-id="element-15441">
                                {slot.price}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-15442">
                              {slot.where}
                            </p>
                            {selectedAdSlot === slot.id && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 animate-in fade-in" data-id="element-15443">
                                <div className="flex items-center gap-2 text-xs" data-id="element-15444">
                                  <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-15445" />
                                  <span className="text-slate-600" data-id="element-15446">
                                    Охват:{' '}
                                    <strong className="text-slate-900" data-id="element-15447">
                                      {slot.reach}
                                    </strong>
                                  </span>
                                </div>
                                <div className="flex items-start gap-2 text-xs" data-id="element-15448">
                                  <AlertCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-15449" />
                                  <span className="text-slate-600" data-id="element-15450">
                                    Требования:{' '}
                                    <strong className="text-slate-900" data-id="element-15451">
                                      {slot.requirements}
                                    </strong>
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>
                      </button>)}
                  </div>

                  <div className="flex justify-end pt-2" data-id="element-15452">
                    <Button variant="primary" disabled={!selectedAdSlot} onClick={() => {
                const selected = availableAdSlots.find(s => s.id === selectedAdSlot);
                if (selected?.slotType === 'Pinned') {
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setShowPinModal(true);
                  setSelectedPinCategory('Натальная карта');
                } else {
                  setAdWizardStep(2);
                }
              }} icon={<ArrowRight className="w-4 h-4" data-id="element-15454" />} data-id="element-15453">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 2 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-15455">
                  <div data-id="element-15456">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-15457">
                      Заголовок рекламы
                    </label>
                    <input type="text" value={adMaterials.title} onChange={e => setAdMaterials({
                ...adMaterials,
                title: e.target.value
              })} placeholder="Например: Профессиональный разбор натальной карты" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-15458" />
                    <div className="text-right text-xs text-slate-400 mt-1" data-id="element-15459">
                      {adMaterials.title.length}/50
                    </div>
                  </div>

                  <div data-id="element-15460">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-15461">
                      Описание
                    </label>
                    <textarea value={adMaterials.description} onChange={e => setAdMaterials({
                ...adMaterials,
                description: e.target.value
              })} placeholder="Кратко опишите ваше предложение..." rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-id="element-15462" />
                  </div>

                  <div data-id="element-15463">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-15464">
                      Ссылка (куда вести)
                    </label>
                    <input type="text" value={adMaterials.link} onChange={e => setAdMaterials({
                ...adMaterials,
                link: e.target.value
              })} placeholder="https://astroguide.ru/profile/elena" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-15465" />
                  </div>

                  <div data-id="element-15466">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-15467">
                      Баннер / Изображение
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 hover:border-indigo-300 cursor-pointer transition-colors relative overflow-hidden group" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const url = URL.createObjectURL(file);
                    setAdMaterials({
                      ...adMaterials,
                      imageUrl: url
                    });
                    showToast(`Изображение загружено ✓`);
                  }
                };
                input.click();
              }} data-id="element-15468">
                      {adMaterials.imageUrl ? <>
                          <img src={adMaterials.imageUrl} alt="Banner" className="absolute inset-0 w-full h-full object-cover" data-id="element-15469" />
                          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-15470">
                            <span className="text-white font-medium text-sm" data-id="element-15471">
                              Заменить изображение
                            </span>
                          </div>
                        </> : <>
                          <LayoutGrid className="w-8 h-8 text-slate-400 mx-auto mb-2" data-id="element-15472" />
                          <div className="text-sm font-bold text-slate-700 mb-1" data-id="element-15473">
                            Загрузить изображение
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-15474">
                            JPG, PNG до 5 МБ
                          </div>
                        </>}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-15475">
                    <Button variant="outline" onClick={() => setAdWizardStep(1)} data-id="element-15476">
                      Назад
                    </Button>
                    <Button variant="primary" disabled={!adMaterials.title || !adMaterials.imageUrl} onClick={() => setAdWizardStep(3)} icon={<ArrowRight className="w-4 h-4" data-id="element-15478" />} data-id="element-15477">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 3 && (() => {
            const selectedSlot = availableAdSlots.find(s => s.id === selectedAdSlot);
            const price = selectedSlot?.priceNum || 0;
            const slotName = selectedSlot?.name || 'Реклама';
            const slotType = selectedSlot?.slotType === 'Email' ? 'email' : selectedSlot?.slotType === 'Pinned' ? 'pin' : 'banner';
            return <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-15479">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-15480">
                        <h4 className="font-bold text-slate-900 mb-3" data-id="element-15481">
                          Сводка заказа
                        </h4>

                        <div className="space-y-2 mb-4" data-id="element-15482">
                          <div className="flex justify-between text-sm" data-id="element-15483">
                            <span className="text-slate-500" data-id="element-15484">Формат</span>
                            <span className="font-medium text-slate-900" data-id="element-15485">
                              {slotName}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-15486">
                            <span className="text-slate-500" data-id="element-15487">Период</span>
                            <span className="font-medium text-slate-900" data-id="element-15488">
                              1 неделя
                            </span>
                          </div>
                        </div>

                        <div className="p-3 bg-white rounded-lg border border-slate-200 mb-4" data-id="element-15489">
                          <div className="text-xs text-slate-500 mb-2" data-id="element-15490">
                            Превью:
                          </div>
                          <div className="flex gap-3 items-center" data-id="element-15491">
                            <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-15492">
                              {adMaterials.imageUrl && <img src={adMaterials.imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-15493" />}
                            </div>
                            <div className="min-w-0" data-id="element-15494">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-15495">
                                {adMaterials.title}
                              </div>
                              <div className="text-xs text-slate-500 truncate" data-id="element-15496">
                                {adMaterials.description || 'Без описания'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-15497">
                          <span className="font-bold text-slate-900" data-id="element-15498">
                            К оплате
                          </span>
                          <span className="text-xl font-bold text-indigo-600" data-id="element-15499">
                            {price.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-15500">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-2" data-id="element-15501">
                          <span className="text-sm font-bold text-indigo-900 flex items-center gap-2" data-id="element-15502">
                            <Wallet className="w-4 h-4" data-id="element-15503" /> Рекламный баланс
                          </span>
                          <span className="font-bold text-indigo-700" data-id="element-15504">
                            {adBalance.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>

                        {adBalance < price ? <div className="text-xs text-rose-600 mt-2 flex items-start gap-1" data-id="element-15505">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-15506" />
                            <span data-id="element-15507">
                              Недостаточно средств. Пополните баланс на{' '}
                              {(price - adBalance).toLocaleString('ru-RU')} ₽
                            </span>
                          </div> : <div className="text-xs text-indigo-600 mt-2" data-id="element-15508">
                            Остаток после оплаты:{' '}
                            {(adBalance - price).toLocaleString('ru-RU')} ₽
                          </div>}
                      </div>

                      <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-15509">
                        <Button variant="outline" onClick={() => setAdWizardStep(2)} data-id="element-15510">
                          Назад
                        </Button>
                        <Button variant="primary" disabled={adBalance < price} onClick={() => {
                  const newOrder: AdOrder = {
                    id: `ORD-A-00${adOrders.length + 1}`,
                    type: slotType as any,
                    name: slotName,
                    status: 'pending',
                    startDate: new Date().toLocaleDateString('ru-RU'),
                    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU'),
                    price: price,
                    views: 0,
                    clicks: 0,
                    orders: 0,
                    income: 0
                  };
                  setAdOrders([newOrder, ...adOrders]);
                  setAdBalance(adBalance - price);
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setAdWizardStep(1);
                  setAdMaterials({
                    title: '',
                    description: '',
                    link: '',
                    imageUrl: ''
                  });
                  showToast('Заказ оплачен и отправлен на модерацию. Вы получите уведомление о статусе.');
                }} data-id="element-15511">
                          Оплатить и отправить
                        </Button>
                      </div>
                    </div>;
          })()}
            </div>
          </div>
        </div>}

      {/* Pin Purchase Modal */}
      {showPinModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-15512">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-15513">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-15514">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-15515">
                Купить размещение
              </h3>
              <button onClick={() => setShowPinModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-15516">
                <X className="w-5 h-5" data-id="element-15517" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-15518">
              <div data-id="element-15519">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-15520">
                  Категория
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={selectedPinCategory} onChange={e => setSelectedPinCategory(e.target.value)} data-id="element-15521">
                  <option value="Натальная карта" data-id="element-15522">Натальная карта</option>
                  <option value="Совместимость" data-id="element-15523">Совместимость</option>
                  <option value="Соляр" data-id="element-15524">Соляр на год</option>
                  <option value="Транзиты" data-id="element-15525">Транзиты</option>
                </select>
              </div>

              <div data-id="element-15526">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-15527">
                  Длительность
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-15528">
                  {[{
                value: '1week' as const,
                label: '1 неделя',
                price: '2 000 ₽'
              }, {
                value: '2weeks' as const,
                label: '2 недели',
                price: '3 500 ₽'
              }, {
                value: '1month' as const,
                label: '1 месяц',
                price: '6 000 ₽'
              }].map(opt => <button key={opt.value} onClick={() => setSelectedPinDuration(opt.value)} className={`p-3 rounded-xl border text-center transition-all ${selectedPinDuration === opt.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-15529">
                      <div className="text-sm font-bold text-slate-900" data-id="element-15530">
                        {opt.label}
                      </div>
                      <div className="text-xs text-indigo-600 font-medium mt-1" data-id="element-15531">
                        {opt.price}
                      </div>
                    </button>)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-15532">
                <div className="flex justify-between text-sm mb-2" data-id="element-15533">
                  <span className="text-slate-500" data-id="element-15534">Категория</span>
                  <span className="font-medium text-slate-900" data-id="element-15535">
                    {selectedPinCategory}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-15536">
                  <span className="font-bold text-slate-900" data-id="element-15537">К оплате</span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-15538">
                    {selectedPinDuration === '1week' ? '2 000' : selectedPinDuration === '2weeks' ? '3 500' : '6 000'}{' '}
                    ₽
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-15539">
                <Button variant="outline" fullWidth onClick={() => setShowPinModal(false)} data-id="element-15540">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={handleBuyPin} data-id="element-15541">
                  Оплатить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}